import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, Observable, retry, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { HereKey } from '../../core/interfaces/here.interface';

/**
 * Service responsible for fetching and storing the HERE Maps API key securely.
 */
@Injectable({
  providedIn: 'root',
})
export class MapApiService {
  private httpClient = inject(HttpClient);

  private _key = signal<string | null>(null);

  /**
   * Gets the HERE Maps API key. Throws an error if the key is not available.
   * @returns The HERE Maps API key as a string.
   * @throws Error if the HERE Maps API key is not available.
   */
  get key(): string {
    const currentKey = this._key();
    if (!currentKey) throw new Error('Here Maps API key is not available');
    return currentKey;
  }

  /**
   * Initializes the API key by fetching it from the backend.
   * This should be called during app initialization.
   */
  initialize(): Observable<HereKey> {
    return this.getApiKey();
  }

  /**
   * Gets the API key from our backend to not expose it in the frontend code.
   * @returns An Observable of the HereKey containing the API key.
   */
  private getApiKey() {
    return this.httpClient.get<HereKey>(environment.missionApi + '/v1/map').pipe(
      retry(2), // Retry up to 2 times on failure
      tap((response) => this._key.set(response.apiKey)),
      catchError((error) => {
        console.error(error);
        return throwError(() => new Error('Unable to load HERE Maps API key'));
      }),
    );
  }
}
